import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { PAGE_ROUTE } from '../lib/routes';
import { AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react';
interface ResetPasswordTokenProps {
  navigate: (page: string, context?: any) => void;
  context?: {
    token?: string;
    email?: string;
  };
}
export function ResetPasswordToken({
  navigate,
  context
}: ResetPasswordTokenProps) {
  const token = context?.token || '';
  const email = context?.email || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Pentru prototip: simulăm token expirat dacă token-ul este exact "expired"
  const isExpired = token === 'expired';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Parolele nu se potrivesc.');
      return;
    }
    if (password.length < 8) {
      setError('Parola trebuie să aibă cel puțin 8 caractere.');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess(true);
    } catch {
      setError('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };
  if (isExpired) {
    return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3980">
        <div className="rounded-2xl border border-dark-4 bg-dark-2/50 p-6 sm:p-8 text-center" data-id="element-3981">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-3982">
            <AlertTriangleIcon className="w-6 h-6 text-red-400" data-id="element-3983" />
          </div>
          <h1 className="text-xl font-bold text-light-1 mb-2" data-id="element-3984">
            Link expirat sau invalid
          </h1>
          <p className="text-sm text-light-3 mb-6 leading-relaxed" data-id="element-3985">
            Acest link de resetare a parolei a expirat sau a fost deja folosit.
            Din motive de securitate, link-urile sunt valabile doar 1 oră.
          </p>
          <Button onClick={() => navigate(PAGE_ROUTE.RESET_PASSWORD)} className="w-full bg-primary text-primary-foreground hover:bg-lime-2" data-id="element-3986">
            Solicită un link nou
          </Button>
        </div>
      </div>;
  }
  if (success) {
    return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3987">
        <div className="rounded-2xl border border-dark-4 bg-dark-2/50 p-6 sm:p-8 text-center" data-id="element-3988">
          <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4" data-id="element-3989">
            <CheckCircle2Icon className="w-6 h-6 text-emerald-400" data-id="element-3990" />
          </div>
          <h1 className="text-xl font-bold text-light-1 mb-2" data-id="element-3991">
            Parolă actualizată
          </h1>
          <p className="text-sm text-light-3 mb-6 leading-relaxed" data-id="element-3992">
            Parola ta a fost schimbată cu succes. Acum te poți autentifica
            folosind noua parolă.
          </p>
          <Button onClick={() => navigate(PAGE_ROUTE.LOGIN, {
          passwordReset: true
        })} className="w-full bg-primary text-primary-foreground hover:bg-lime-2" data-id="element-3993">
            Mergi la autentificare
          </Button>
        </div>
      </div>;
  }
  return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3994">
      {/* Dev helper pentru a testa starea de expirat */}
      <div className="mb-4 flex justify-end" data-id="element-3995">
        <button onClick={() => navigate(PAGE_ROUTE.RESET_PASSWORD_TOKEN, {
        token: 'expired'
      })} className="text-[10px] uppercase tracking-wider font-mono text-light-4 hover:text-light-2 border border-dark-4 px-2 py-1 rounded bg-dark-2 transition-colors" data-id="element-3996">
          [Dev] Testează link expirat
        </button>
      </div>

      <div className="text-center mb-8" data-id="element-3997">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3998">Setează parola nouă</h1>
        <p className="text-sm text-light-3 mt-2" data-id="element-3999">
          Introdu noua ta parolă mai jos.
        </p>
      </div>

      <div className="rounded-2xl border border-dark-4 bg-dark-2/50 p-6 sm:p-8" data-id="element-4000">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-4001">
          {error && <div className="p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm" data-id="element-4002">
              {error}
            </div>}

          <div className="space-y-1.5" data-id="element-4003">
            <label htmlFor="new-password" className="block text-sm font-medium text-light-2" data-id="element-4004">
              Parolă nouă
            </label>
            <Input id="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={8} autoComplete="new-password" data-id="element-4005" />
          </div>

          <div className="space-y-1.5" data-id="element-4006">
            <label htmlFor="confirm-password" className="block text-sm font-medium text-light-2" data-id="element-4007">
              Confirmă parola
            </label>
            <Input id="confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required minLength={8} autoComplete="new-password" data-id="element-4008" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-lime-2" size="lg" data-id="element-4009">
            {loading ? <span className="flex items-center gap-2" data-id="element-4010">
                <Spinner size="sm" data-id="element-4011" />
                Se salvează...
              </span> : 'Salvează parola'}
          </Button>
        </form>

        <p className="text-center text-sm text-light-4 mt-4" data-id="element-4012">
          <button onClick={() => navigate(PAGE_ROUTE.LOGIN)} className="font-medium text-lime-1 hover:text-lime-2" data-id="element-4013">
            Înapoi la autentificare
          </button>
        </p>
      </div>
    </div>;
}